import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { VeteranVerifyDialog } from "../components/VeteranVerifyDialog";
import {
  computeQuote, money, ROOF_LABELS, FREQ_LABELS, ADDON_LABELS, ADDONS, SLOT_LABELS,
} from "../lib/pricing";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import {
  ArrowLeft, ArrowRight, Check, MapPin, CalendarDays, ShieldCheck, CreditCard, Loader2,
} from "lucide-react";

const ROOF_OPTS = ["single", "two_story", "steep"];
const FREQ_OPTS = ["one_time", "biannual", "quarterly"];
const STEPS = ["Configuration", "Address", "Schedule", "Review & Pay"];

export default function BookingWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const preset = location.state || {};
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  const vetEligible = user?.veteran_verified && !user?.first_cleaning_used;

  const [data, setData] = useState({
    panels: preset.panels || 16,
    roof_type: preset.roof_type || "single",
    frequency: preset.frequency || "one_time",
    addons: preset.addons || [],
    is_veteran: (preset.is_veteran && vetEligible) || false,
    address: "",
    city: "",
    access_notes: "",
    service_date: "",
    time_slot: "morning",
  });

  const q = useMemo(
    () => computeQuote(data.panels, data.roof_type, data.frequency, data.addons, data.is_veteran && vetEligible),
    [data, vetEligible]
  );

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const toggleAddon = (a) => set({ addons: data.addons.includes(a) ? data.addons.filter((x) => x !== a) : [...data.addons, a] });

  const canNext = () => {
    if (step === 1) return data.address.trim() && data.city.trim();
    if (step === 2) return data.service_date;
    return true;
  };

  const today = new Date().toISOString().split("T")[0];

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", data);
      const { data: checkout } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <VeteranVerifyDialog open={showVerify} setOpen={setShowVerify} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-6" data-testid="booking-back"><ArrowLeft className="w-4 h-4" /> Cancel</button>
        <h1 className="font-heading text-3xl font-black text-slate-900">Book your cleaning</h1>

        {/* stepper */}
        <div className="flex items-center gap-2 mt-6 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${i < step ? "bg-emerald-700 text-white" : i === step ? "bg-emerald-700 text-white ring-4 ring-emerald-100" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-sm font-semibold hidden sm:block ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < step ? "bg-emerald-700" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="booking-step-panel">
          {step === 0 && (
            <div className="space-y-8">
              <div>
                <div className="flex items-end justify-between mb-3">
                  <Label className="font-heading font-bold text-slate-900">Number of panels</Label>
                  <span className="font-mono text-2xl font-bold text-emerald-700" data-testid="booking-panel-count">{data.panels}</span>
                </div>
                <Slider value={[data.panels]} min={8} max={60} step={1} onValueChange={(v) => set({ panels: v[0] })} data-testid="booking-panel-slider" />
              </div>
              <div>
                <Label className="font-heading font-bold text-slate-900 block mb-3">Roof type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ROOF_OPTS.map((r) => (
                    <button key={r} onClick={() => set({ roof_type: r })} data-testid={`booking-roof-${r}`}
                      className={`px-3 py-3 rounded-xl text-sm font-semibold border transition-colors ${data.roof_type === r ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>{ROOF_LABELS[r]}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-heading font-bold text-slate-900 block mb-3">Frequency</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {FREQ_OPTS.map((f) => (
                    <button key={f} onClick={() => set({ frequency: f })} data-testid={`booking-freq-${f}`}
                      className={`px-3 py-3 rounded-xl text-sm font-semibold border transition-colors ${data.frequency === f ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>{FREQ_LABELS[f]}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-heading font-bold text-slate-900 block mb-3">Add-ons</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.keys(ADDONS).map((a) => (
                    <button key={a} onClick={() => toggleAddon(a)} data-testid={`booking-addon-${a}`}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold border transition-colors ${data.addons.includes(a) ? "bg-emerald-50 text-emerald-800 border-emerald-400" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
                      <span>{ADDON_LABELS[a]}</span><span className="font-mono">+{money(ADDONS[a])}</span></button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold"><MapPin className="w-5 h-5" /> Service address</div>
              <div><Label htmlFor="addr">Street address</Label><Input id="addr" data-testid="booking-address" value={data.address} onChange={(e) => set({ address: e.target.value })} className="mt-1.5 h-11" placeholder="1234 Sunbeam Ave" /></div>
              <div><Label htmlFor="city">City / ZIP</Label><Input id="city" data-testid="booking-city" value={data.city} onChange={(e) => set({ city: e.target.value })} className="mt-1.5 h-11" placeholder="Austin, TX 78701" /></div>
              <div><Label htmlFor="notes">Roof access notes (optional)</Label><Textarea id="notes" data-testid="booking-notes" value={data.access_notes} onChange={(e) => set({ access_notes: e.target.value })} className="mt-1.5" placeholder="Gate code, dog in yard, ladder access on the east side…" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CalendarDays className="w-5 h-5" /> Pick a date & arrival window</div>
              <div><Label htmlFor="date">Service date</Label><Input id="date" type="date" min={today} data-testid="booking-date" value={data.service_date} onChange={(e) => set({ service_date: e.target.value })} className="mt-1.5 h-11" /></div>
              <div>
                <Label className="block mb-3">Arrival window</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.keys(SLOT_LABELS).map((s) => (
                    <button key={s} onClick={() => set({ time_slot: s })} data-testid={`booking-slot-${s}`}
                      className={`px-4 py-4 rounded-xl text-sm font-semibold border transition-colors ${data.time_slot === s ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>{SLOT_LABELS[s]}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              {vetEligible && (
                <div className="flex items-center justify-between bg-slate-900 rounded-xl px-5 py-4">
                  <div className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-amber-400" /><div><div className="text-white font-bold text-sm">Apply veteran discount</div><div className="text-slate-400 text-xs">15% off your first cleaning</div></div></div>
                  <Switch checked={data.is_veteran} onCheckedChange={(v) => set({ is_veteran: v })} data-testid="booking-veteran-toggle" />
                </div>
              )}
              {user?.is_veteran && !user?.veteran_verified && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                    <div>
                      <div className="text-slate-900 font-bold text-sm">Unlock your 15% veteran discount</div>
                      <div className="text-slate-500 text-xs">Verify your service to apply the discount</div>
                    </div>
                  </div>
                  <Button type="button" size="sm" onClick={() => setShowVerify(true)} data-testid="booking-verify-veteran-btn" className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">Verify</Button>
                </div>
              )}
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-2.5 text-sm" data-testid="booking-summary">
                <Row l="Panels" v={`${data.panels} · ${ROOF_LABELS[data.roof_type]}`} />
                <Row l="Frequency" v={FREQ_LABELS[data.frequency]} />
                {data.addons.length > 0 && <Row l="Add-ons" v={data.addons.map((a) => ADDON_LABELS[a]).join(", ")} />}
                <Row l="Address" v={`${data.address}, ${data.city}`} />
                <Row l="When" v={`${data.service_date} · ${SLOT_LABELS[data.time_slot]}`} />
                <div className="border-t border-slate-200 pt-3 mt-3 space-y-2">
                  <Row l="Base service" v={money(q.base)} mono />
                  {q.addonTotal > 0 && <Row l="Add-ons" v={money(q.addonTotal)} mono />}
                  {q.discount > 0 && <div className="flex justify-between text-amber-600 font-semibold"><span>Veteran discount</span><span className="font-mono">−{money(q.discount)}</span></div>}
                </div>
                <div className="flex items-end justify-between border-t border-slate-200 pt-3">
                  <span className="font-bold text-slate-900">Total due</span>
                  <span className="text-3xl font-black font-mono text-emerald-700" data-testid="booking-total">{money(q.total)}</span>
                </div>
              </div>
            </div>
          )}

          {/* nav */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            <Button variant="ghost" onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))} data-testid="booking-prev-btn" className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
            {step < 3 ? (
              <Button onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete this step")} data-testid="booking-next-btn" className="rounded-full bg-emerald-700 hover:bg-emerald-800 gap-2 px-6">Continue <ArrowRight className="w-4 h-4" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} data-testid="booking-pay-btn" className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold gap-2 px-6">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay {money(q.total)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, mono }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{l}</span>
      <span className={`text-slate-900 font-medium text-right ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );
}
